// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { KeyRound, User as UserIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../api/auth';

const FIELDS = [
  { name: 'first_name', label: 'First name', required: true },
  { name: 'middle_name', label: 'Middle name' },
  { name: 'last_name', label: 'Last name' },
  { name: 'phone', label: 'Phone' },
  { name: 'mobile', label: 'Mobile' },
  { name: 'birth_date', label: 'Date of birth', type: 'date' },
];

export default function ProfilePage() {
  const { updateProfile } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    authAPI
      .getProfile()
      .then((res) => {
        setProfile(res.data);
        setForm(res.data);
      })
      .catch(() => setError('Failed to load profile.'))
      .finally(() => setLoading(false));
  }, []);

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const data = await updateProfile(form);
      setProfile(data.user ?? data);
      setForm(data.user ?? data);
      setSuccess('Profile updated.');
    } catch (err) {
      const fieldErrors = err?.response?.data;
      const firstError =
        fieldErrors && typeof fieldErrors === 'object'
          ? Object.values(fieldErrors).flat()[0]
          : null;
      setError(firstError || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
        Loading profile…
      </div>
    );
  }

  return (
    <div className="max-w-lg space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <UserIcon size={18} className="text-gray-400" />
          Profile
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Your account details. This information is only visible to you.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
            {error}
          </p>
        )}
        {success && (
          <p className="text-sm text-green-600 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-4">
            {success}
          </p>
        )}

        {!profile ? null : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Email
              </label>
              <input
                type="email"
                value={form.email ?? ''}
                onChange={set('email')}
                required
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {FIELDS.map(({ name, label, type = 'text', required }) => (
              <div key={name}>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {label}
                </label>
                <input
                  type={type}
                  value={form[name] ?? ''}
                  onChange={set(name)}
                  required={required}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            ))}

            <button
              type="submit"
              disabled={saving}
              className="w-full py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-60 transition-colors"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </form>
        )}
      </div>

      <Link
        to="/updatepassword"
        className="flex items-center gap-2.5 px-4 py-3 bg-white rounded-2xl border border-gray-200 shadow-sm text-sm text-gray-700 hover:bg-gray-50 w-fit"
      >
        <KeyRound size={16} className="text-gray-400" />
        Change password
      </Link>
    </div>
  );
}
